<template>
  <div class="xy-text" :style="dirStyle" ref="componentRef">
    <p :style="wordStyle">
      <span ref="textRef" :style="spanStyle">{{ text }}</span>
    </p>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, computed, onMounted, onUpdated } from 'vue'
import { info, defaultProps } from './config'
import { formatDateByLanguage, isTimestamp } from '@/packages/plugins/dateUtils'
import { manageLineClamp } from '@/packages/plugins/domUtils'
import { checkVersion } from '@/packages/plugins/versions'

defineOptions({ name: info.name })
const props = defineProps(defaultProps)
const text = ref(props.text)
const language = ref('en')
const componentRef = ref()
const textRef = ref()

/**
 * 判断字符串是否包含中文字符
 * @param {string} str - 要判断的字符串
 * @returns {boolean} - 如果字符串包含中文字符，则返回 true；否则返回 false
 */
const containsChinese = (str) => {
  const chineseRegex = /[\u4e00-\u9fa5]/
  return chineseRegex.test(str)
}

const spanStyle = computed(() => {
  if (props.element.style.textDecoration) {
    return {
      textDecoration: props.element.style.textDecoration
    }
  } else {
    return {}
  }
})

const dirStyle = computed(() => {
  const style = {
    direction: language.value === 'ar' ? 'rtl' : 'ltr'
  }
  // 版本兼容校验
  if (checkVersion(2)) {
    style.display = 'flex'
    style.alignItems = 'center'
  }
  if (props.autoHeight) {
    // 自适应高度
    style.height = 'auto'
  } else {
    style.overflow = 'hidden'
  }
  if (props.autoWidth) {
    style.width = 'auto'
  }
  return style
})

const wordStyle = computed(() => {
  // 除了中文, 单词固定在一行
  const style = {
    wordBreak: containsChinese(text.value) ? 'break-all' : 'keep-all'
  }
  if (props.autoHeight) {
    // 自适应高度
    style.height = 'auto'
  }
  if (props.autoWidth) {
    style.width = 'auto'
    style.wordBreak = 'keep-all'
  }
  return style
})

watch(
  () => [props.text, props.isFormatTime, props.format, props.enableEllipsis],
  ([val, isFormatTimeVal, formatVal, enableEllipsisVal]) => {
    nextTick(() => {
      manageLineClamp(enableEllipsisVal, textRef.value, componentRef.value)
      if (isFormatTimeVal && val) {
        text.value = isTimestamp(val) ? formatDateByLanguage(val, language.value, formatVal || 'MM-DD', formatVal || 'DD-MM') : val
        return
      }
      text.value = val
    })
  },
  { immediate: true, deep: true }
)

watch(
  () => props.element.style,
  () => {
    nextTick(() => {
      manageLineClamp(props.enableEllipsis, textRef.value, componentRef.value)
    })
  },
  { immediate: true, deep: true }
)

onMounted(() => {
  if (props.enableEllipsis) {
    nextTick(() => {
      manageLineClamp(true, textRef.value, componentRef.value)
    })
  }
  if (componentRef.value) {
    language.value = componentRef.value.getAttribute('language') || 'en'
  }
})
onUpdated(() => {
  if (componentRef.value) {
    language.value = componentRef.value.getAttribute('language') || 'en'
  }
})
</script>

<style lang="scss" scoped>
.xy-text {
  position: relative;

  p {
    width: 100%;
    height: auto;
    word-break: break-all;
    white-space: initial;

    span {
      display: inline-block;
    }
  }
}
</style>
